import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Settings, AlertCircle, MapPin, Wrench, ArrowRight, Check } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Prestations maintenance (sourced brief FLOW_OK : préventive et curative, toute la France)
const services = [
  {
    icon: Settings,
    title: "Maintenance préventive",
    desc: "Contrats de maintenance périodique : contrôles, nettoyages, vérifications fluides et paramètres pour sécuriser la performance de vos installations dans la durée."
  },
  {
    icon: AlertCircle,
    title: "Maintenance curative",
    desc: "Dépannage et interventions correctives en cas de panne ou de dysfonctionnement, avec diagnostic technique et remise en service."
  },
  {
    icon: MapPin,
    title: "Intervention toute la France",
    desc: "FLOW intervient sur l'ensemble du territoire français pour la maintenance et le dépannage de vos systèmes de climatisation et ventilation."
  },
  {
    icon: Wrench,
    title: "Techniciens qualifiés",
    desc: "Nos techniciens interviennent sur les principales marques du marché : Daikin, Mitsubishi, Hitachi, Bosch."
  }
];

const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function MaintenancePage() {
  return (
    <>
      <Helmet>
        <title>Maintenance climatisation Paris | FLOW</title>
        <meta name="description" content="Maintenance préventive et curative de systèmes HVAC par FLOW. Dépannage et interventions sur toute la France. Contrats adaptés à chaque type d'installation." />
        <link rel="canonical" href="https://flow-air.com/maintenance" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="maintenance-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Service — Maintenance</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Maintenance et <span className="text-[#00AFFF]">dépannage HVAC</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW assure la maintenance préventive et curative de vos systèmes de climatisation
              et ventilation. Nos équipes interviennent sur toute la France.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Prestations */}
      <section className="py-16 md:py-24 bg-white" data-testid="maintenance-services">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Nos prestations</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Préventif, curatif et dépannage
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`maintenance-service-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{service.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{service.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Badges */}
      <section className="py-12 bg-[#F5F7FA]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-lg shadow-sm">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Besoin d'une intervention ?
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez FLOW pour un contrat de maintenance ou une intervention de dépannage.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="maintenance-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
